import { useQuery } from '@tanstack/react-query';
import ApiService, { ApiError } from '../services/api.service';

export interface NodeDataResponse {
  total_records: number;
  total_pages: number;
  records: Record<string, any>[];
}

export interface UseNodeDataOptions {
  nodeId: string | null;
  page?: number;
  limit?: number;
  search?: string;
  sort?: string;
  enabled?: boolean;
}

export function useNodeData({
  nodeId,
  page = 1,
  limit = 25,
  search = '',
  sort = '',
  enabled = true,
}: UseNodeDataOptions) {
  return useQuery<NodeDataResponse>({
    queryKey: ['node-data', nodeId, page, limit, search, sort],
    queryFn: async () => {
      if (!nodeId) {
        throw new Error('Node ID is required');
      }

      const params: Record<string, any> = {
        page,
        limit,
      };

      if (search) {
        params.q = search;
      }

      if (sort) {
        params.sort = sort;
      }

      const apiService = ApiService.getInstance();
      const response = await apiService.request(
        'GET',
        `/pipelinenodes/${nodeId}/data`,
        params
      ) as NodeDataResponse;

      return response;
    },
    enabled: enabled && !!nodeId,
    staleTime: 30 * 1000, // 30 seconds
    retry: (failureCount, error) => {
      // Don't retry on 400, 422, or 404 errors (client errors)
      if (error instanceof ApiError) {
        const statusCode = error.code;
        if (statusCode === 400 || statusCode === 422 || statusCode === 404) {
          return false;
        }
      }
      // Retry other errors up to 3 times
      return failureCount < 3;
    },
  });
}

